import { ApiError, RequestTimeoutError } from '@/core/api/http'
import {
  anlegenTeilnehmendePerson,
  isZuweisungslisteResult,
  zuweisungslisteErrorBody,
  type TeilnehmendePerson,
} from '../../api/zuweisungsliste'

export const PERSON_FELDER = [
  'kundennummer',
  'anrede',
  'vorname',
  'nachname',
  'organisationszeichen',
  'beginn',
  'ende',
] as const satisfies readonly (keyof TeilnehmendePerson)[]

export type AnlegenErgebnis =
  | { kind: 'angelegt' }
  | { kind: 'uebersprungen'; kundennummer: string }
  | { kind: 'abgelehnt'; meldung: string; felder: Map<string, string>; sonstige: string[] }
  | { kind: 'unlesbar' }
  | { kind: 'fehler'; meldung: string }

const FORM_FELDER: ReadonlySet<string> = new Set(PERSON_FELDER)

function fehlerMeldung(error: unknown): string {
  const body = zuweisungslisteErrorBody(error)
  if (body) return body.meldung
  if (error instanceof ApiError) {
    return `Anlegen fehlgeschlagen (Fehler ${error.status}). Bitte versuchen Sie es später erneut.`
  }
  if (error instanceof RequestTimeoutError) return error.message
  return "Anlegen fehlgeschlagen. Bitte prüfen Sie Ihre Internetverbindung und versuchen Sie es erneut."
}

export async function teilnehmendePersonAnlegen(person: TeilnehmendePerson): Promise<AnlegenErgebnis> {
  let result: unknown
  try {
    result = await anlegenTeilnehmendePerson(person)
  } catch (e) {
    if (e instanceof SyntaxError) return { kind: 'unlesbar' }
    if (e instanceof ApiError && e.status === 422) {
      const body = zuweisungslisteErrorBody(e)
      if (body) {
        const felder = new Map<string, string>()
        const sonstige: string[] = []
        for (const f of body.fehler) {
          if (f.feld && FORM_FELDER.has(f.feld) && !felder.has(f.feld)) felder.set(f.feld, f.grund)
          else sonstige.push(f.grund)
        }
        return { kind: 'abgelehnt', meldung: body.meldung, felder, sonstige }
      }
    }
    console.error("Teilnehmende Person anlegen fehlgeschlagen:", e)
    return { kind: 'fehler', meldung: fehlerMeldung(e) }
  }
  if (!isZuweisungslisteResult(result)) return { kind: 'unlesbar' }
  if (result.uebersprungen.length > 0) {
    return { kind: 'uebersprungen', kundennummer: result.uebersprungen[0].kundennummer }
  }
  if (result.angelegt !== 1) return { kind: 'unlesbar' }
  return { kind: 'angelegt' }
}
